// =====================================================================
// Erreurs.jsx — M-006 (29-05-2026)
// Journal d'erreurs opérationnelles. Tabs : Dashboard / Mes erreurs /
// Toutes les erreurs / Nouvelle erreur / Typologies.
// Permission gates: errors.read.own / read.all / create.all / manage_catalog.all
// =====================================================================

import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../hooks/useAuth.jsx';
import { errors as errorsApi, projects as projectsApi, users as usersApi } from '../../services/api.js';
import Breadcrumbs from '../../components/Breadcrumbs.jsx';
import ErrorFormModal from './ErrorFormModal.jsx';
import ErrorDashboard from './ErrorDashboard.jsx';
import ErrorsList from './ErrorsList.jsx';
import TypologiesTab from './TypologiesTab.jsx';

const VALID_TABS = new Set(['dashboard', 'mes', 'toutes', 'nouvelle', 'typologies']);

export default function Erreurs() {
  const { user, can } = useAuth();
  const canReadAll = can('errors.read.all');
  const canCreate = can('errors.create.all');
  const canManageCatalog = can('errors.manage_catalog.all');

  // Defaults
  const defaultTab = canReadAll ? 'dashboard' : 'mes';
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [showCreate, setShowCreate] = useState(false);
  const [editingError, setEditingError] = useState(null);

  // Data caches
  const [typologies, setTypologies] = useState([]);
  const [projects, setProjects] = useState([]);
  const [allUsers, setAllUsers] = useState([]);

  useEffect(() => {
    errorsApi.listTypologies(true).then(setTypologies).catch(() => setTypologies([]));
    // TOUS les projets : une erreur de mars peut porter sur un client archivé
    // depuis, et le filtre « Tous clients » doit pouvoir la retrouver. Les
    // archivés sont marqués, et non proposés pour une NOUVELLE erreur.
    projectsApi.list().then(d => setProjects(Array.isArray(d) ? d : (d?.items || []))).catch(() => setProjects([]));
    if (canCreate || canReadAll) {
      // Comptes INACTIFS compris : une erreur enregistrée en mars appartient
      // toujours à son auteur, même s'il est parti depuis. (L'appel portait
      // `isActive: true`, une clé inconnue du serveur, jetée en silence.)
      usersApi.list().then(d => setAllUsers(Array.isArray(d) ? d : (d?.items || d?.users || []))).catch(() => setAllUsers([]));
    }
  }, [canCreate, canReadAll]);

  // Sync hash → tab
  useEffect(() => {
    const h = (location.hash || '').replace('#', '');
    if (VALID_TABS.has(h)) setActiveTab(h);
  }, []);

  // Mark seen when opening "Mes erreurs" tab
  useEffect(() => {
    if (activeTab === 'mes') {
      errorsApi.markSeen().catch(() => {});
    }
  }, [activeTab]);

  const TAB_LABELS = {
    dashboard:  'Tableau de bord',
    mes:        'Mes erreurs',
    toutes:     'Toutes les erreurs',
    nouvelle:   'Nouvelle erreur',
    typologies: 'Typologies (catalogue)',
  };

  function refreshAll() {
    // simple way: force re-render via key
    setRefreshKey(k => k + 1);
  }
  const [refreshKey, setRefreshKey] = useState(0);

  function handleCreateClick() {
    setEditingError(null);
    setShowCreate(true);
  }

  function handleEdit(err) {
    setEditingError(err);
    setShowCreate(true);
  }

  async function handleSaved() {
    setShowCreate(false);
    setEditingError(null);
    refreshAll();
  }

  return (
    <div style={{ padding: '1.5rem 2rem' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
        <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 600 }}>🚨 Erreurs</h1>
        <span style={{ fontSize: '0.75rem', color: '#9ca3af', fontFamily: 'monospace' }}>M-006 — journal d'erreurs opérationnelles</span>
      </div>
      <p className="hide-mobile" style={{ color: '#6b7280', fontSize: '0.875rem', margin: '0 0 1.25rem' }}>
        Encodage et suivi des erreurs opérationnelles par opérateur, typologie et client.
      </p>

      <Breadcrumbs items={[
        { label: 'Erreurs', onClick: () => setActiveTab(defaultTab) },
        { label: TAB_LABELS[activeTab] || activeTab },
      ]} />

      {/* Tabs */}
      <div className="mobile-tabs-scroll" style={{ borderBottom: '1px solid #e5e7eb', marginBottom: '1.5rem', display: 'flex', gap: 4 }}>
        {canReadAll && (
          <button style={tabBtn(activeTab === 'dashboard')} onClick={() => setActiveTab('dashboard')}>
            📊 Tableau de bord
          </button>
        )}
        <button style={tabBtn(activeTab === 'mes')} onClick={() => setActiveTab('mes')}>
          👤 Mes erreurs
        </button>
        {canReadAll && (
          <button style={tabBtn(activeTab === 'toutes')} onClick={() => setActiveTab('toutes')}>
            📋 Toutes les erreurs
          </button>
        )}
        {canCreate && (
          <button style={tabBtn(activeTab === 'nouvelle')} onClick={handleCreateClick}>
            + Nouvelle erreur
          </button>
        )}
        {canManageCatalog && (
          <button style={tabBtn(activeTab === 'typologies')} onClick={() => setActiveTab('typologies')}>
            🏷️ Typologies
          </button>
        )}
      </div>

      {/* Tab content */}
      <div key={refreshKey}>
        {activeTab === 'dashboard' && canReadAll && <ErrorDashboard typologies={typologies} users={allUsers} />}
        {activeTab === 'mes' && (
          <ErrorsList
            mode="mine"
            typologies={typologies}
            canEdit={false}  // operateurul nu poate modifica
            canDelete={false}
          />
        )}
        {activeTab === 'toutes' && canReadAll && (
          <ErrorsList
            mode="all"
            typologies={typologies}
            users={allUsers}
            projects={projects}
            canEdit={can('errors.update.all')}
            canDelete={can('errors.delete.all')}
            onEdit={handleEdit}
            onCreate={handleCreateClick}
          />
        )}
        {activeTab === 'typologies' && canManageCatalog && <TypologiesTab />}
      </div>

      {showCreate && (
        <ErrorFormModal
          editing={editingError}
          typologies={typologies}
          users={allUsers}
          projects={projects}
          onSaved={handleSaved}
          onClose={() => { setShowCreate(false); setEditingError(null); }}
        />
      )}
    </div>
  );
}

function tabBtn(active) {
  return {
    padding: '9px 18px', border: 'none', background: 'transparent',
    fontSize: 13, fontWeight: 600, cursor: 'pointer',
    color: active ? '#4338ca' : '#64748b',
    borderBottom: active ? '2px solid #4338ca' : '2px solid transparent',
    fontFamily: 'inherit',
  };
}
